<div class="enroll-mfa text-center">
  <h2 class="enroll-title">Set up two-factor authentication</h2>

  <p class="instructions">
    Protect your account with a second verification step. Choose how you want
    to receive your verification codes.
  </p>

  <!-- Invisible reCAPTCHA anchor for SMS enrollment -->
  <div id="enroll-mfa-recaptcha-container"></div>

  @if (infoMessage) {
    <div class="success-message" role="status">{{ infoMessage }}</div>
  }

  @if (errorMessage) {
    <div class="server-error" role="alert">{{ errorMessage }}</div>
  }

  <!-- Re-auth required: enrollment needs a recent sign-in -->
  @if (requiresReauth) {
    <div class="d-grid gap-2 mt-3">
      <button
        type="button"
        class="btn btn-primary btn-fill btn-danger"
        (click)="onSignOut()"
      >
        Sign out and sign back in
      </button>
    </div>
  } @else {
    <!-- Already-enrolled factors -->
    @if (enrolledFactors.length > 0) {
      <div class="enrolled-factors text-start">
        <h3 class="enrolled-title">Enrolled methods</h3>
        <ul class="enrolled-list">
          @for (factor of enrolledFactors; track factor.uid) {
            <li>{{ factor.displayName || factor.factorId }}</li>
          }
        </ul>
      </div>
      <div class="d-grid gap-2 mt-2">
        <button
          type="button"
          class="btn btn-primary btn-fill btn-danger"
          (click)="onContinue()"
        >
          Continue to dashboard
        </button>
      </div>
    }

    <!-- Method selection -->
    @if (!method) {
      <div class="d-grid gap-2 mt-3">
        <button
          type="button"
          class="btn btn-primary btn-fill"
          (click)="selectMethod('phone')"
          [disabled]="loading"
        >
          Text message (SMS)
        </button>
        <button
          type="button"
          class="btn btn-primary btn-fill"
          data-testid="choose-totp"
          (click)="selectMethod('totp')"
          [disabled]="loading || !totpAvailable"
          [attr.aria-describedby]="totpAvailable ? null : 'enrollTotpUnavailable'"
        >
          Authenticator app (TOTP)
        </button>
        @if (!totpAvailable) {
          <p id="enrollTotpUnavailable" class="small text-center mb-0 totp-unavailable" data-testid="totp-unavailable">
            Authenticator apps aren't available in local emulator mode. Use a text message.
          </p>
        }
      </div>
    }

    <!-- Phone (SMS) enrollment -->
    @if (method === 'phone') {
      @if (!phoneCodeSent) {
        <form class="enroll-form mt-3" (ngSubmit)="sendPhoneCode()">
          <label class="form-label" for="enrollPhoneNumber">
            Phone number (include country code)
          </label>
          <input
            id="enrollPhoneNumber"
            class="form-control text-center"
            type="tel"
            autocomplete="tel"
            placeholder="+15551234567"
            [formControl]="phoneControl"
          />
          @if (
            phoneControl.invalid && (phoneControl.dirty || phoneControl.touched)
          ) {
            <div class="server-error" role="alert">
              Enter a valid phone number in E.164 format, e.g. +15551234567.
            </div>
          }
          <div class="d-grid gap-2 mt-3">
            <button
              type="submit"
              class="btn btn-primary btn-fill btn-danger"
              [disabled]="loading || phoneControl.invalid"
            >
              @if (loading) {
                Sending code...
              } @else {
                Send verification code
              }
            </button>
          </div>
        </form>
      } @else {
        <form class="enroll-form mt-3" (ngSubmit)="confirmPhoneCode()">
          <label class="form-label" for="enrollPhoneCode">
            Enter the 6-digit code we sent to your phone
          </label>
          <input
            id="enrollPhoneCode"
            class="form-control code-input text-center"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            placeholder="123456"
            [formControl]="codeControl"
          />
          @if (
            codeControl.invalid && (codeControl.dirty || codeControl.touched)
          ) {
            <div class="server-error" role="alert">
              Enter the 6-digit verification code.
            </div>
          }
          <div class="d-grid gap-2 mt-3">
            <button
              type="submit"
              class="btn btn-primary btn-fill btn-danger"
              [disabled]="loading || codeControl.invalid"
            >
              @if (loading) {
                Verifying...
              } @else {
                Verify and enable
              }
            </button>
            <button
              type="button"
              class="btn btn-primary btn-sm btn-fill"
              (click)="sendPhoneCode()"
              [disabled]="loading"
            >
              Resend code
            </button>
          </div>
        </form>
      }
      <button
        type="button"
        class="btn btn-link back-link"
        (click)="cancelMethod()"
        [disabled]="loading"
      >
        Choose a different method
      </button>
    }

    <!-- Authenticator app (TOTP) enrollment -->
    @if (method === 'totp') {
      @if (totpQrCodeUrl) {
        <div class="totp-setup mt-3">
          <div class="qr-container">
            <img
              [src]="totpQrCodeUrl"
              alt="QR Code for Authenticator App"
              class="qr-code"
            />
            <p class="qr-hint">
              Scan with Google Authenticator, Authy, or a similar app.
            </p>
          </div>

          @if (totpSecretKey) {
            <div class="secret-key">
              <span class="secret-label">Manual entry key</span>
              <code class="secret-value">{{ totpSecretKey }}</code>
              <p class="qr-hint">Use this if you can't scan the QR code.</p>
            </div>
          }

          <form class="enroll-form mt-3" (ngSubmit)="confirmTotpCode()">
            <label class="form-label" for="enrollTotpCode">
              Enter the 6-digit code from your authenticator app
            </label>
            <input
              id="enrollTotpCode"
              class="form-control code-input text-center"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              maxlength="6"
              placeholder="123456"
              [formControl]="codeControl"
            />
            @if (
              codeControl.invalid && (codeControl.dirty || codeControl.touched)
            ) {
              <div class="server-error" role="alert">
                Enter the 6-digit verification code.
              </div>
            }
            <div class="d-grid gap-2 mt-3">
              <button
                type="submit"
                class="btn btn-primary btn-fill btn-danger"
                [disabled]="loading || codeControl.invalid"
              >
                @if (loading) {
                  Verifying...
                } @else {
                  Verify and enable
                }
              </button>
            </div>
          </form>
        </div>
      } @else if (loading) {
        <p class="instructions mt-3">Generating your QR code...</p>
      }
      <button
        type="button"
        class="btn btn-link back-link"
        (click)="cancelMethod()"
        [disabled]="loading"
      >
        Choose a different method
      </button>
    }
  }
</div>
